import I18n from "pro-features/components/WithTranslation/src/I18n";
import { StatefulDropdown } from "app/components/dashboard/widget-wizard/components/StatefulDropdown";
import {
    booleanSearchApiParamsToChips,
    booleanSearchChipsObjectToApiParams,
} from "app/pages/website-analysis/traffic-sources/search/booleanSearchUtility";
import type { FC } from "react";
import { useState } from "react";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { BooleanSearch } from "@similarweb/ui-components";
import { BooleanSearchWrapper } from "../widget-types/TableSearchKeywordsDashboardWidgetFilters";
import type { IBooleanSearchChipItem } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import type { IDynamicFilterProps } from "app/components/dashboard/widget-wizard/components/DashboardWizardDynamicFilters";

const SORT_FILTER = "orderBy";
const SEARCH_TERMS_FILTER_RAW = "searchTerms";
const INCLUDE_TERMS_FILTER = "IncludeTerms";
const EXCLUDE_TERMS_FILTER = "ExcludeTerms";
const INCLUDE_SUBDOMAINS_FILTER = "includeSubDomains";

interface IFilterObject {
    [SORT_FILTER]: string;
    [INCLUDE_TERMS_FILTER]: string;
    [EXCLUDE_TERMS_FILTER]: string;
    [INCLUDE_SUBDOMAINS_FILTER]: string;
}

const PaidSearchAdsTableWidgetFilters: FC<IDynamicFilterProps> = (props) => {
    const { widgetFilters: filterObject, changeFilter: onFilterChangeCallback } = props;
    const [searchTerms, setSearchTerms] = useState({ includeTerms: "", excludeTerms: "" });

    const onFilterChange = (
        filterName: keyof IFilterObject | string,
        value: string | boolean | IBooleanSearchChipItem[],
    ) => {
        let newData;
        if (filterName === SEARCH_TERMS_FILTER_RAW) {
            const { IncludeTerms = "", ExcludeTerms = "" } = booleanSearchChipsObjectToApiParams(
                value as IBooleanSearchChipItem[],
            );
            newData = { ...filterObject, IncludeTerms, ExcludeTerms };
            setSearchTerms((searchTerms) => ({
                ...searchTerms,
                includeTerms: IncludeTerms,
                excludeTerms: ExcludeTerms,
            }));
        } else {
            newData = { ...filterObject, [filterName]: value };
        }

        const updatedFilters = Object.entries(newData)
            .filter(([key, value]) => !!value)
            .map(([key, value]) => ({ name: key, value }));

        onFilterChangeCallback(updatedFilters);
    };

    return (
        <div
            key="dynamicFiltersContainer"
            className="filters"
        >
            <div
                key={SORT_FILTER}
                className="dynamicFilter"
            >
                <h5>
                    <I18n>{`home.dashboards.wizard.filters.orderBy`}</I18n>
                </h5>
                <StatefulDropdown
                    key={`${SORT_FILTER}Filter`}
                    items={[
                        {
                            id: "TrafficShare desc",
                            text: translate("pla.ads.page.ads.table.columns.share.title"),
                        },
                        {
                            id: "Volume desc",
                            text: translate("pla.ads.page.ads.table.columns.volume.title"),
                        },
                    ]}
                    selectedId={filterObject[SORT_FILTER] ?? "TrafficShare desc"}
                    onSelect={(value) => onFilterChange(SORT_FILTER, value.id)}
                />
            </div>
            <div
                key={INCLUDE_SUBDOMAINS_FILTER}
                className="dynamicFilter"
            >
                <h5>
                    <I18n>{`home.dashboards.wizard.filters.includeSubDomains`}</I18n>
                </h5>
                <StatefulDropdown
                    key={`${INCLUDE_SUBDOMAINS_FILTER}Filter`}
                    items={[
                        { id: "true", text: translate("global.yes") },
                        { id: "false", text: translate("global.no") },
                    ]}
                    selectedId={filterObject[INCLUDE_SUBDOMAINS_FILTER]}
                    onSelect={(value) => onFilterChange(INCLUDE_SUBDOMAINS_FILTER, value.id)}
                />
            </div>
            <FlexColumn
                key={INCLUDE_TERMS_FILTER}
                className="filters"
            >
                <h5>
                    <I18n>{`home.dashboards.wizard.filters.searchterm`}</I18n>
                </h5>
                <BooleanSearchWrapper>
                    <BooleanSearch
                        onChange={(value) => onFilterChange(SEARCH_TERMS_FILTER_RAW, value)}
                        chips={booleanSearchApiParamsToChips({
                            IncludeTerms: searchTerms.includeTerms || filterObject.IncludeTerms,
                            ExcludeTerms: searchTerms.excludeTerms || filterObject.ExcludeTerms,
                        })}
                    />
                </BooleanSearchWrapper>
            </FlexColumn>
        </div>
    );
};

export default PaidSearchAdsTableWidgetFilters;
